import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material"
import DownloadIcon from "@mui/icons-material/Download"
import { useSnackbar } from "notistack"
import i18n from "i18n"
import ExcelDropzone from "components/Dropzone/ExcelDropzone"
import {
  useDownloadImportTemplate,
  useImportContacts,
} from "./hooks/useContacts"

const ImportContactsModal = ({ open, onClose, organizationId, onImported }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [selectedFiles, setSelectedFiles] = useState([])
  const [notExcel, setNotExcel] = useState(false)
  const downloadTemplate = useDownloadImportTemplate()
  const importContacts = useImportContacts()

  useEffect(() => {
    if (selectedFiles.length === 0) setNotExcel(false)
  }, [selectedFiles.length])

  const handleClose = () => {
    if (importContacts.isLoading) return
    setSelectedFiles([])
    setNotExcel(false)
    downloadTemplate.reset()
    importContacts.reset()
    onClose()
  }

  const handleDownloadTemplate = () => {
    downloadTemplate.mutate(undefined, {
      onError: () => {
        enqueueSnackbar(i18n.t("contacts.import.templateError"), {
          variant: "error",
        })
      },
    })
  }

  const handleUpload = () => {
    if (selectedFiles.length === 0 || notExcel) return
    importContacts.mutate(
      { organizationId, file: selectedFiles[0] },
      {
        onSuccess: result => {
          const created = result?.created ?? 0
          const skipped = result?.skipped ?? 0
          const errorCount = result?.errors?.length ?? 0
          const messageKey =
            errorCount > 0
              ? "contacts.import.successWithErrors"
              : "contacts.import.success"
          enqueueSnackbar(
            i18n.t(messageKey, { created, skipped, errors: errorCount }),
            { variant: errorCount > 0 ? "warning" : "success" },
          )
          onImported?.(result)
          setSelectedFiles([])
          setNotExcel(false)
          onClose()
        },
        onError: () => {
          enqueueSnackbar(i18n.t("contacts.import.error"), {
            variant: "error",
          })
        },
      },
    )
  }

  const isUploading = importContacts.isLoading
  const isDownloading = downloadTemplate.isLoading

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("contacts.import.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ mb: 2, color: "#414141" }}>
          {i18n.t("contacts.import.description")}
        </Typography>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="text"
            color="secondary"
            onClick={handleDownloadTemplate}
            disabled={isDownloading || isUploading}
            startIcon={
              isDownloading ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            sx={{ textTransform: "none", paddingLeft: 0 }}
          >
            {isDownloading
              ? i18n.t("contacts.import.downloadingTemplate")
              : i18n.t("contacts.import.downloadTemplate")}
          </Button>
        </Box>
        <ExcelDropzone
          selectedFiles={selectedFiles}
          setselectedFiles={setSelectedFiles}
          setNotExcel={setNotExcel}
          i18nTitle="contacts.import.dragDropHint"
          i18nDescription="contacts.import.validFormats"
        />
        {notExcel && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {i18n.t("contacts.import.invalidFormat")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading} color="secondary">
          {i18n.t("contacts.import.cancel")}
        </Button>
        <Button
          variant="contained"
          color="branded"
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || isUploading || notExcel}
          startIcon={isUploading ? <CircularProgress size={16} /> : null}
        >
          {isUploading
            ? i18n.t("contacts.import.uploading")
            : i18n.t("contacts.import.upload")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ImportContactsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
  onImported: PropTypes.func,
}

export default ImportContactsModal
